import { useQuery } from '@tanstack/react-query'
import qs from 'query-string'
import { RemirrorJSON } from 'remirror'

import axios from '@pepper/utils/src/function/axios/editorInstance'
import {
  getIsPublicDocFromAPIType,
  getURLFromAPIType,
} from '@pepper/utils/src/function/getEditorApiEndpoint'

// state time when content is not fetched
const STALE_TIME_MS = 1 * 60 * 60 * 1000 // 1 hour

export function useDocsContent(props: Parameters) {
  return useQuery({
    queryKey: [
      'docs',
      props.data.id,
      { excludeContent: props.data.excludeContent },
    ],
    queryFn: () => handleApiRequest(props.data),
    staleTime: props.data.excludeContent ? STALE_TIME_MS : 0,
    refetchInterval: props.data.excludeContent ? STALE_TIME_MS : false,
    refetchIntervalInBackground: props.data.excludeContent ? true : false,
  })
}

async function handleApiRequest(data: Parameters['data']) {
  if (!data.id) throw new Error('Cannot find document id')
  const url = `/${getURLFromAPIType(data.apiType)}/${data.id}/editor`
  const endpoint = qs.stringifyUrl(
    {
      url,
      query: {
        is_public: getIsPublicDocFromAPIType(data.apiType),
        exclude_content: data.excludeContent,
      },
    },
    { skipNull: true, skipEmptyString: true }
  )
  // pass skip check to avoid token check as this api is used to fetch editor token
  const response = await axios.get<Response>(endpoint, {
    params: { skipTokenCheck: true },
  })
  return response.data
}

type Parameters = {
  data: {
    id: string | undefined
    apiType: string
    excludeContent?: boolean
  }
}

type Response = {
  content: {
    html: string
    text: string
    json: RemirrorJSON
  }
  pepper_doc_id: string
  'x-editor-token': string
  private_doc?: {
    code: number
    is_renamed: boolean
    title: string
  }
  assignment?: {
    assignment_code: number
    title: string
  }
  project: {
    code: number
    id: string
    company: {
      code: number
      id: string
    }
  }
  public_access: ContentPublicAccess
  is_yjs_enabled?: boolean
  use_yjs_v2?: boolean
  metadata?: {
    title: string
  }
}
